import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Plus,
  Trash2,
  Eye,
} from 'lucide-react';
import {
  api,
  type AccountRow,
  type GcalIntegration,
  type GcalPreview,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function GoogleCalendar(): JSX.Element {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [newAccount, setNewAccount] = useState('');

  const { data: integrationsData, isLoading } = useQuery({
    queryKey: ['gcal'],
    queryFn: () => api.get<{ integrations: GcalIntegration[] }>('/admin/gcal'),
    refetchInterval: 30_000,
  });
  const integrations = integrationsData?.integrations ?? [];

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<AccountRow[]>('/admin/accounts'),
  });

  const { data: serviceEmail } = useQuery({
    queryKey: ['gcal-service-email'],
    queryFn: () => api.get<{ email: string | null }>('/admin/gcal/service-email'),
  });

  const createMutation = useMutation({
    mutationFn: (kommo_account_id: string) =>
      api.post<{ id: string }>('/admin/gcal', { kommo_account_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gcal'] });
      setCreateOpen(false);
      setNewAccount('');
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Google Calendar</h1>
          <p className="text-sm text-muted-foreground">
            Виджет Kommo Dashboard со счётчиком Viewings + Meetings из Google Calendar.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button disabled={accounts.length === 0}>
              <Plus className="h-4 w-4" />
              Подключить
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newAccount) createMutation.mutate(newAccount);
              }}
              className="space-y-4"
            >
              <DialogHeader>
                <DialogTitle>Новая интеграция</DialogTitle>
                <DialogDescription>
                  Один integration на kommo-аккаунт. Внутри можно добавить несколько календарей —
                  виджет покажет суммарную цифру.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="account">Kommo аккаунт</Label>
                <select
                  id="account"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={newAccount}
                  onChange={(e) => setNewAccount(e.target.value)}
                  required
                >
                  <option value="">— выбрать —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.display_name} ({a.subdomain})
                    </option>
                  ))}
                </select>
              </div>
              {createMutation.error && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                  {(createMutation.error as Error).message}
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Создаём…' : 'Создать'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {serviceEmail?.email && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 pt-6 text-sm">
            <CheckCircle2 className="h-4 w-4 text-success" />
            <div className="flex-1">
              Service-account email — на него нужно расшарить календари с доступом «See all event
              details»:
            </div>
            <code className="rounded bg-muted px-2 py-1 font-mono text-xs">
              {serviceEmail.email}
            </code>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigator.clipboard.writeText(serviceEmail.email ?? '')}
              title="Скопировать"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">Загрузка…</CardContent>
        </Card>
      )}

      {!isLoading && integrations.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Интеграций нет. Нажмите «Подключить», чтобы создать первую.
          </CardContent>
        </Card>
      )}

      {integrations.map((integ) => (
        <IntegrationCard key={integ.id} integration={integ} />
      ))}
    </div>
  );
}

function IntegrationCard({ integration }: { integration: GcalIntegration }): JSX.Element {
  const queryClient = useQueryClient();
  const [addCalOpen, setAddCalOpen] = useState(false);
  const [calForm, setCalForm] = useState<{
    calendar_id: string;
    display_name: string;
    kommo_user_id: string;
  }>({ calendar_id: '', display_name: '', kommo_user_id: '' });
  const [editForm, setEditForm] = useState<null | {
    keywords_viewing: string;
    keywords_meeting: string;
    lookahead_days: number;
    timezone: string;
  }>(null);

  const editFormCurrent = editForm ?? {
    keywords_viewing: integration.keywords_viewing.join(', '),
    keywords_meeting: integration.keywords_meeting.join(', '),
    lookahead_days: integration.lookahead_days,
    timezone: integration.timezone,
  };

  const patchMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch<{ ok: boolean }>(`/admin/gcal/${integration.id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gcal'] });
      setEditForm(null);
    },
  });

  const deleteIntegrationMutation = useMutation({
    mutationFn: () => api.delete<{ ok: boolean }>(`/admin/gcal/${integration.id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gcal'] }),
  });

  const addCalMutation = useMutation({
    mutationFn: () => {
      const trimmedUid = calForm.kommo_user_id.trim();
      const uid = trimmedUid.length > 0 ? Number(trimmedUid) : null;
      return api.post<{ id: string }>(`/admin/gcal/${integration.id}/calendars`, {
        calendar_id: calForm.calendar_id.trim(),
        display_name: calForm.display_name.trim(),
        kommo_user_id: uid !== null && Number.isFinite(uid) ? uid : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gcal'] });
      setAddCalOpen(false);
      setCalForm({ calendar_id: '', display_name: '', kommo_user_id: '' });
    },
  });

  const removeCalMutation = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/admin/gcal/calendars/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gcal'] }),
  });

  const syncUsersMutation = useMutation({
    mutationFn: () =>
      api.post<{
        matched: Array<{ calendar_id: string; user_id: number }>;
        skipped: Array<{ calendar_id: string; reason: string }>;
        kommo_users_total: number;
      }>(`/admin/gcal/${integration.id}/sync-kommo-users`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gcal'] }),
  });

  const [previewPeriod, setPreviewPeriod] = useState<'day' | 'week' | 'month'>('week');
  const {
    data: preview,
    refetch: refetchPreview,
    isFetching: previewLoading,
  } = useQuery({
    queryKey: ['gcal-preview', integration.id, previewPeriod],
    queryFn: () =>
      api.get<GcalPreview>(`/admin/gcal/${integration.id}/preview?period=${previewPeriod}`),
    enabled: false,
  });

  function saveEdits(): void {
    const data = editFormCurrent;
    patchMutation.mutate({
      keywords_viewing: data.keywords_viewing.split(',').map((s) => s.trim()).filter(Boolean),
      keywords_meeting: data.keywords_meeting.split(',').map((s) => s.trim()).filter(Boolean),
      lookahead_days: data.lookahead_days,
      timezone: data.timezone,
    });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">{integration.account_display_name}</CardTitle>
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={integration.is_active ? 'success' : 'secondary'}>
              {integration.is_active ? 'Active' : 'Paused'}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {integration.calendars.length} календарь(-я)
            </span>
          </div>
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              patchMutation.mutate({ is_active: !integration.is_active })
            }
          >
            {integration.is_active ? 'Pause' : 'Resume'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (window.confirm('Удалить интеграцию? Календари тоже удалятся.')) {
                deleteIntegrationMutation.mutate();
              }
            }}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <section className="grid gap-3 md:grid-cols-2">
          <Field label="Ключевые слова Viewing (через запятую)">
            <Input
              value={editFormCurrent.keywords_viewing}
              onChange={(e) =>
                setEditForm({ ...editFormCurrent, keywords_viewing: e.target.value })
              }
            />
          </Field>
          <Field label="Ключевые слова Meeting (через запятую)">
            <Input
              value={editFormCurrent.keywords_meeting}
              onChange={(e) =>
                setEditForm({ ...editFormCurrent, keywords_meeting: e.target.value })
              }
            />
          </Field>
          <Field label="Lookahead, дней">
            <Input
              type="number"
              min={1}
              max={365}
              value={editFormCurrent.lookahead_days}
              onChange={(e) =>
                setEditForm({
                  ...editFormCurrent,
                  // при очистке поля Number('') = NaN → падаем на 1, чтобы не слать NaN
                  lookahead_days: Number(e.target.value) || 1,
                })
              }
            />
          </Field>
          <Field label="Timezone (IANA, например Europe/London)">
            <Input
              value={editFormCurrent.timezone}
              onChange={(e) =>
                setEditForm({ ...editFormCurrent, timezone: e.target.value })
              }
            />
          </Field>
        </section>
        {editForm && (
          <div className="flex gap-2">
            <Button onClick={saveEdits} disabled={patchMutation.isPending}>
              {patchMutation.isPending ? 'Сохраняем…' : 'Сохранить настройки'}
            </Button>
            <Button variant="outline" onClick={() => setEditForm(null)}>
              Отмена
            </Button>
          </div>
        )}

        <section>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-medium">Календари</div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => syncUsersMutation.mutate()}
                disabled={syncUsersMutation.isPending}
                title="Подставить kommo_user_id, сопоставив calendar_id с email пользователей Kommo"
              >
                {syncUsersMutation.isPending ? 'Матчим…' : 'Сопоставить с Kommo'}
              </Button>
              <Dialog open={addCalOpen} onOpenChange={setAddCalOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline">
                    <Plus className="h-3.5 w-3.5" />
                    Добавить календарь
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    addCalMutation.mutate();
                  }}
                  className="space-y-4"
                >
                  <DialogHeader>
                    <DialogTitle>Новый календарь</DialogTitle>
                    <DialogDescription>
                      Calendar ID можно найти в Google Calendar → Settings → Integrate calendar.
                      Не забудьте расшарить календарь на service-account email.
                    </DialogDescription>
                  </DialogHeader>
                  <Field label="Calendar ID">
                    <Input
                      value={calForm.calendar_id}
                      onChange={(e) => setCalForm({ ...calForm, calendar_id: e.target.value })}
                      placeholder="name@example.com или xxx@group.calendar.google.com"
                      required
                    />
                  </Field>
                  <Field label="Имя (опционально)">
                    <Input
                      value={calForm.display_name}
                      onChange={(e) => setCalForm({ ...calForm, display_name: e.target.value })}
                      placeholder="Например: Anastasia"
                    />
                  </Field>
                  <Field label="Kommo user ID (опционально)">
                    <Input
                      value={calForm.kommo_user_id}
                      onChange={(e) => setCalForm({ ...calForm, kommo_user_id: e.target.value })}
                      placeholder="Если фильтр по сотруднику в Kommo Dashboard — id из Kommo"
                      type="number"
                      min="0"
                    />
                  </Field>
                  {addCalMutation.error && (
                    <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                      {(addCalMutation.error as Error).message}
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setAddCalOpen(false)}>
                      Отмена
                    </Button>
                    <Button type="submit" disabled={addCalMutation.isPending}>
                      {addCalMutation.isPending ? 'Добавляем…' : 'Добавить'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
              </Dialog>
            </div>
          </div>
          {syncUsersMutation.data && (
            <div className="mb-2 rounded-md border bg-muted/30 px-3 py-2 text-xs">
              Сопоставлено{' '}
              <span className="font-semibold">{syncUsersMutation.data.matched.length}</span> /{' '}
              {syncUsersMutation.data.matched.length + syncUsersMutation.data.skipped.length}.{' '}
              {syncUsersMutation.data.skipped.length > 0 && (
                <span className="text-muted-foreground">
                  Пропущено: {syncUsersMutation.data.skipped.map((s) => s.calendar_id).join(', ')}
                </span>
              )}
            </div>
          )}
          {syncUsersMutation.error && (
            <div className="mb-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {(syncUsersMutation.error as Error).message}
            </div>
          )}
          {integration.calendars.length === 0 ? (
            <div className="rounded-md border border-dashed py-6 text-center text-xs text-muted-foreground">
              Календарей пока нет.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Имя</TableHead>
                  <TableHead>Calendar ID</TableHead>
                  <TableHead className="w-32">Kommo user</TableHead>
                  <TableHead className="w-20 text-right">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {integration.calendars.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.display_name || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{c.calendar_id}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {c.kommo_user_id ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCalMutation.mutate(c.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        <section>
          <div className="mb-2 text-sm font-medium">URL виджета для Kommo</div>
          <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-3 font-mono text-xs">
            <span className="flex-1 truncate">{integration.widget_url}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigator.clipboard.writeText(integration.widget_url)}
              title="Скопировать"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button asChild variant="ghost" size="sm">
              <a href={integration.widget_url} target="_blank" rel="noreferrer" title="Открыть">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            Kommo автоматически добавит <code>?kommo_period=day|week|month</code> к URL.
          </div>
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-medium">Preview</div>
            <div className="flex items-center gap-2">
              <select
                value={previewPeriod}
                onChange={(e) =>
                  setPreviewPeriod(e.target.value as 'day' | 'week' | 'month')
                }
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              >
                <option value="day">День</option>
                <option value="week">Неделя</option>
                <option value="month">Месяц</option>
              </select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void refetchPreview()}
                disabled={previewLoading}
              >
                <Eye className="h-3.5 w-3.5" />
                {previewLoading ? 'Загрузка…' : 'Проверить'}
              </Button>
            </div>
          </div>
          {preview && (
            <div className="rounded-md border bg-muted/20 p-3">
              <div className="mb-2 text-sm">
                <span className="font-bold">{preview.total}</span> событий
              </div>
              <div className="space-y-1">
                {preview.by_calendar.map((c) => (
                  <div
                    key={c.calendar_id}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="font-mono">
                      {c.display_name || c.calendar_id}
                    </span>
                    <span
                      className={
                        c.ok ? 'text-muted-foreground' : 'flex items-center gap-1 text-destructive'
                      }
                    >
                      {c.ok ? (
                        `${c.count} events`
                      ) : (
                        <>
                          <AlertCircle className="h-3 w-3" />
                          {c.error}
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}
